import React, { useState, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { API_URL } from '../config';
import { formatMoney } from '../utils/money';

const TEXT = {
  es: {
    openButton: 'Importar pedidos',
    title: 'Importar pedidos',
    intro: 'Sube la exportaci\u00f3n de pedidos de Shopify (se detecta sola) o un .csv/.xlsx con la plantilla. Una fila por art\u00edculo: las filas con el mismo n\u00famero de pedido se agrupan en un solo pedido.',
    notes: 'El stock no se modifica (son ventas pasadas). Los pedidos que ya existan se omiten. Si un SKU no est\u00e1 en tu cat\u00e1logo, se crea el producto con costo 0 para que lo completes. Solo se admiten pedidos en la moneda de tu empresa.',
    template: 'Descargar plantilla',
    templateFile: 'plantilla_pedidos.csv',
    templateLines: [
      'pedido;fecha;canal;estado;cliente;email;sku;producto;cantidad;precio_unitario;envio;comision;iva;total;moneda',
      'P-1001;15/09/2026;WooCommerce;enviado;Ana Garc\u00eda;ana@ejemplo.com;CAM-001;Camiseta b\u00e1sica negra;2;14,90;4,95;;7,75;44,65;EUR',
      'P-1001;;;;;;GOR-002;Gorra negra;1;9,90;;;;;',
      'P-1002;16/09/2026;Instagram;pagado;Luis P\u00e9rez;;CAM-001;Camiseta b\u00e1sica negra;1;14,90;3,50;;;18,40;EUR',
    ],
    chooseFile: 'Elegir archivo',
    analyzing: 'Analizando archivo...',
    applying: 'Guardando pedidos...',
    formatShopify: 'Formato detectado: exportaci\u00f3n de pedidos de Shopify',
    formatTemplate: 'Formato detectado: plantilla',
    toCreate: 'Se importar\u00e1n',
    duplicates: 'Ya exist\u00edan (se omiten)',
    errors: 'Con errores',
    newProducts: 'Productos nuevos',
    unknownColumns: 'Columnas ignoradas (no reconocidas):',
    withoutItems: '{{count}} pedido(s) se importar\u00e1n sin art\u00edculos: su ganancia real no tendr\u00e1 costo de producto.',
    newProductsNote: 'Estos productos se crear\u00e1n con costo 0. Compl\u00e9talos despu\u00e9s en Productos para que la ganancia sea real.',
    errorsTitle: 'Pedidos con errores (no se importar\u00e1n)',
    warningsTitle: 'Avisos',
    ordersTitle: 'Pedidos que se importar\u00e1n',
    newProductsTitle: 'Productos que se crear\u00e1n',
    row: 'Fila',
    order: 'Pedido',
    date: 'Fecha',
    channel: 'Canal',
    status: 'Estado',
    total: 'Total',
    items: 'Art\u00edculos',
    sku: 'SKU',
    name: 'Nombre',
    message: 'Problema',
    truncated: 'Se muestran solo las primeras 200 filas de cada lista.',
    confirm: 'Confirmar importaci\u00f3n',
    cancel: 'Cerrar',
    nothing: 'No hay pedidos nuevos que importar.',
    success: 'Importaci\u00f3n completada: {{orders}} pedidos y {{products}} productos nuevos.',
    genericError: 'No se pudo procesar el archivo.',
    statuses: { paid: 'Pagado', shipped: 'Enviado', pending: 'Pendiente', cancelled: 'Cancelado' },
  },
  en: {
    openButton: 'Import orders',
    title: 'Import orders',
    intro: 'Upload your Shopify orders export (detected automatically) or a .csv/.xlsx using the template. One row per item: rows with the same order number are grouped into one order.',
    notes: 'Stock is not changed (these are past sales). Orders that already exist are skipped. If a SKU is not in your catalog, the product is created with cost 0 so you can complete it. Only orders in your business currency are supported.',
    template: 'Download template',
    templateFile: 'orders_template.csv',
    templateLines: [
      'order_id,date,channel,status,customer,email,sku,product,quantity,unit_price,shipping,fees,tax,total,currency',
      'P-1001,2026-09-15,WooCommerce,shipped,Ana Garcia,ana@example.com,TSH-001,Basic black t-shirt,2,14.90,4.95,,7.75,44.65,EUR',
      'P-1001,,,,,,CAP-002,Black cap,1,9.90,,,,,',
      'P-1002,2026-09-16,Instagram,paid,Luis Perez,,TSH-001,Basic black t-shirt,1,14.90,3.50,,,18.40,EUR',
    ],
    chooseFile: 'Choose file',
    analyzing: 'Analyzing file...',
    applying: 'Saving orders...',
    formatShopify: 'Detected format: Shopify orders export',
    formatTemplate: 'Detected format: template',
    toCreate: 'Will be imported',
    duplicates: 'Already existed (skipped)',
    errors: 'With errors',
    newProducts: 'New products',
    unknownColumns: 'Ignored columns (not recognized):',
    withoutItems: '{{count}} order(s) will be imported without items: their real profit will have no product cost.',
    newProductsNote: 'These products will be created with cost 0. Complete them later in Products so your profit is accurate.',
    errorsTitle: 'Orders with errors (will not be imported)',
    warningsTitle: 'Warnings',
    ordersTitle: 'Orders to import',
    newProductsTitle: 'Products to create',
    row: 'Row',
    order: 'Order',
    date: 'Date',
    channel: 'Channel',
    status: 'Status',
    total: 'Total',
    items: 'Items',
    sku: 'SKU',
    name: 'Name',
    message: 'Problem',
    truncated: 'Only the first 200 rows of each list are shown.',
    confirm: 'Confirm import',
    cancel: 'Close',
    nothing: 'There are no new orders to import.',
    success: 'Import completed: {{orders}} orders and {{products}} new products.',
    genericError: 'The file could not be processed.',
    statuses: { paid: 'Paid', shipped: 'Shipped', pending: 'Pending', cancelled: 'Cancelled' },
  },
  fr: {
    openButton: 'Importer des commandes',
    title: 'Importer des commandes',
    intro: 'Importez l\u2019export des commandes Shopify (d\u00e9tect\u00e9 automatiquement) ou un .csv/.xlsx avec le mod\u00e8le. Une ligne par article : les lignes avec le m\u00eame num\u00e9ro de commande sont regroup\u00e9es.',
    notes: 'Le stock n\u2019est pas modifi\u00e9 (ce sont des ventes pass\u00e9es). Les commandes d\u00e9j\u00e0 existantes sont ignor\u00e9es. Si un SKU n\u2019est pas dans votre catalogue, le produit est cr\u00e9\u00e9 avec un co\u00fbt de 0 pour que vous le compl\u00e9tiez. Seules les commandes dans la devise de votre entreprise sont accept\u00e9es.',
    template: 'T\u00e9l\u00e9charger le mod\u00e8le',
    templateFile: 'modele_commandes.csv',
    templateLines: [
      'commande;date;canal;statut;client;email;sku;produit;quantite;prix_unitaire;livraison;commission;tva;total;devise',
      'P-1001;15/09/2026;WooCommerce;expedie;Ana Garcia;ana@exemple.com;TSH-001;T-shirt basique noir;2;14,90;4,95;;7,75;44,65;EUR',
      'P-1001;;;;;;CAS-002;Casquette noire;1;9,90;;;;;',
      'P-1002;16/09/2026;Instagram;paye;Luis Perez;;TSH-001;T-shirt basique noir;1;14,90;3,50;;;18,40;EUR',
    ],
    chooseFile: 'Choisir un fichier',
    analyzing: 'Analyse du fichier...',
    applying: 'Enregistrement des commandes...',
    formatShopify: 'Format d\u00e9tect\u00e9 : export des commandes Shopify',
    formatTemplate: 'Format d\u00e9tect\u00e9 : mod\u00e8le',
    toCreate: 'Seront import\u00e9es',
    duplicates: 'D\u00e9j\u00e0 existantes (ignor\u00e9es)',
    errors: 'Avec erreurs',
    newProducts: 'Nouveaux produits',
    unknownColumns: 'Colonnes ignor\u00e9es (non reconnues) :',
    withoutItems: '{{count}} commande(s) seront import\u00e9es sans articles : leur b\u00e9n\u00e9fice r\u00e9el n\u2019aura pas de co\u00fbt produit.',
    newProductsNote: 'Ces produits seront cr\u00e9\u00e9s avec un co\u00fbt de 0. Compl\u00e9tez-les ensuite dans Produits pour un b\u00e9n\u00e9fice exact.',
    errorsTitle: 'Commandes avec erreurs (non import\u00e9es)',
    warningsTitle: 'Avertissements',
    ordersTitle: 'Commandes \u00e0 importer',
    newProductsTitle: 'Produits \u00e0 cr\u00e9er',
    row: 'Ligne',
    order: 'Commande',
    date: 'Date',
    channel: 'Canal',
    status: 'Statut',
    total: 'Total',
    items: 'Articles',
    sku: 'SKU',
    name: 'Nom',
    message: 'Probl\u00e8me',
    truncated: 'Seules les 200 premi\u00e8res lignes de chaque liste sont affich\u00e9es.',
    confirm: 'Confirmer l\u2019importation',
    cancel: 'Fermer',
    nothing: 'Aucune nouvelle commande \u00e0 importer.',
    success: 'Importation termin\u00e9e : {{orders}} commandes et {{products}} nouveaux produits.',
    genericError: 'Impossible de traiter le fichier.',
    statuses: { paid: 'Pay\u00e9e', shipped: 'Exp\u00e9di\u00e9e', pending: 'En attente', cancelled: 'Annul\u00e9e' },
  },
};

export const getOrderImportText = (lang) => TEXT[(lang || 'es').slice(0, 2)] || TEXT.es;

const fillTemplate = (text, values) =>
  text.replace(/\{\{(\w+)\}\}/g, (_, key) => (values[key] !== undefined ? String(values[key]) : ''));

function OrderImport({ token, onAuthError, onClose, onImported, currency }) {
  const { i18n } = useTranslation();
  const tx = getOrderImportText(i18n.language);
  const fileInput = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const money = (value) =>
    formatMoney(value, i18n.language, currency);

  const sendFile = async (selectedFile, dryRun) => {
    const formData = new FormData();
    formData.append('file', selectedFile);
    const response = await axios.post(
      `${API_URL}/api/v1/orders/import?dry_run=${dryRun ? 'true' : 'false'}`,
      formData,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data;
  };

  const handleError = (err) => {
    if (err.response && err.response.status === 401) {
      onAuthError();
      return;
    }
    const detail = err.response && err.response.data && err.response.data.detail;
    setError(typeof detail === 'string' ? detail : tx.genericError);
  };

  const handleFileChange = async (e) => {
    const selected = e.target.files && e.target.files[0];
    e.target.value = '';
    if (!selected) return;
    setFile(selected);
    setPreview(null);
    setError('');
    setSuccess('');
    setBusy('analyzing');
    try {
      setPreview(await sendFile(selected, true));
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const confirmImport = async () => {
    if (!file) return;
    setBusy('applying');
    setError('');
    try {
      const result = await sendFile(file, false);
      setSuccess(fillTemplate(tx.success, { orders: result.summary.to_create, products: result.summary.new_products }));
      setPreview(null);
      setFile(null);
      if (onImported) onImported();
    } catch (err) {
      handleError(err);
    } finally {
      setBusy('');
    }
  };

  const downloadTemplate = () => {
    const content = '\uFEFF' + tx.templateLines.map((l) => l.replace(/EUR$/, currency || 'EUR')).join('\r\n') + '\r\n';
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = tx.templateFile;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const summary = preview ? preview.summary : null;
  const nothingToApply = !summary || summary.to_create === 0;
  const truncated = !!summary && (summary.to_create > 200 || summary.errors > 200 || summary.warnings > 200 || summary.new_products > 200);
  const th = 'text-muted text-xs text-left pb-2 pr-4 uppercase';
  const td = 'py-2 pr-4 text-sm';

  const table = (title, titleClass, headers, rows) => (
    <div className="mt-5">
      <h4 className={`text-sm font-bold mb-2 ${titleClass}`}>{title}</h4>
      <div className="max-h-64 overflow-auto">
        <table className="w-full border-collapse">
          <thead>
            <tr className="border-b border-edge">
              {headers.map((h) => (
                <th key={h} className={th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>{rows}</tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="bg-surface border border-edge rounded-2xl p-5 mb-6">
      <h3 className="text-white text-lg font-bold">{tx.title}</h3>
      <p className="text-muted text-sm mt-1">{tx.intro}</p>
      <p className="text-muted text-xs mt-2">{tx.notes}</p>

      <div className="flex flex-wrap gap-2 mt-4">
        <button
          type="button"
          onClick={downloadTemplate}
          className="bg-surfaceAlt border border-edge text-white text-sm px-4 py-2 rounded-lg cursor-pointer"
        >
          {tx.template}
        </button>
        <button
          type="button"
          onClick={() => fileInput.current && fileInput.current.click()}
          disabled={!!busy}
          className="bg-accent text-panel font-bold text-sm px-4 py-2 rounded-lg cursor-pointer"
        >
          {tx.chooseFile}
        </button>
        <input ref={fileInput} type="file" accept=".csv,.xlsx" className="hidden" onChange={handleFileChange} />
      </div>

      {file && <p className="text-muted text-xs mt-2">{file.name}</p>}
      {busy && <p className="text-muted text-sm mt-4">{busy === 'applying' ? tx.applying : tx.analyzing}</p>}
      {error && (
        <div className="bg-[#2a0f0f] border border-[#7f1d1d] text-red-400 text-sm rounded-lg p-3 mt-4">{error}</div>
      )}
      {success && (
        <div className="bg-[#0f2a14] border border-[#1d7f3a] text-accent text-sm rounded-lg p-3 mt-4">{success}</div>
      )}

      {preview && !busy && (
        <div className="mt-5">
          <p className="text-muted text-xs mb-3">{preview.format === 'shopify' ? tx.formatShopify : tx.formatTemplate}</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              [tx.toCreate, summary.to_create, 'text-accent'],
              [tx.duplicates, summary.duplicates, 'text-muted'],
              [tx.errors, summary.errors, summary.errors > 0 ? 'text-red-400' : 'text-muted'],
              [tx.newProducts, summary.new_products, summary.new_products > 0 ? 'text-amber-400' : 'text-muted'],
            ].map(([label, value, color]) => (
              <div key={label} className="bg-surfaceAlt border border-edge rounded-lg p-3">
                <p className="text-muted text-xs">{label}</p>
                <p className={`text-xl font-bold ${color}`}>{value}</p>
              </div>
            ))}
          </div>

          {preview.unknown_columns && preview.unknown_columns.length > 0 && (
            <p className="text-muted text-xs mt-3">
              {tx.unknownColumns} {preview.unknown_columns.join(', ')}
            </p>
          )}

          {summary.without_items > 0 && (
            <div className="bg-[#2a1f0f] border border-[#7f5f1d] rounded-lg p-3 mt-4">
              <p className="text-amber-400 text-sm">{fillTemplate(tx.withoutItems, { count: summary.without_items })}</p>
            </div>
          )}

          {preview.errors.length > 0 &&
            table(tx.errorsTitle, 'text-red-400', [tx.row, tx.order, tx.message], preview.errors.map((item, idx) => (
              <tr key={`e-${idx}`} className="border-b border-surfaceAlt">
                <td className={`${td} text-muted`}>{item.row}</td>
                <td className={`${td} text-[#888] font-mono`}>{item.order_id || '\u2014'}</td>
                <td className={`${td} text-red-400`}>{item.message}</td>
              </tr>
            )))}

          {preview.warnings.length > 0 &&
            table(tx.warningsTitle, 'text-amber-400', [tx.row, tx.order, tx.message], preview.warnings.map((item, idx) => (
              <tr key={`w-${idx}`} className="border-b border-surfaceAlt">
                <td className={`${td} text-muted`}>{item.row}</td>
                <td className={`${td} text-[#888] font-mono`}>{item.order_id}</td>
                <td className={`${td} text-amber-400`}>{item.message}</td>
              </tr>
            )))}

          {preview.new_products.length > 0 && (
            <>
              {table(tx.newProductsTitle, 'text-amber-400', [tx.sku, tx.name], preview.new_products.map((item) => (
                <tr key={`p-${item.sku}`} className="border-b border-surfaceAlt">
                  <td className={`${td} text-[#888] font-mono`}>{item.sku}</td>
                  <td className={`${td} text-white`}>{item.name}</td>
                </tr>
              )))}
              <p className="text-muted text-xs mt-2">{tx.newProductsNote}</p>
            </>
          )}

          {preview.orders.length > 0 &&
            table(tx.ordersTitle, 'text-white', [tx.order, tx.date, tx.channel, tx.status, tx.items, tx.total], preview.orders.map((item) => (
              <tr key={`o-${item.channel}-${item.order_id}`} className="border-b border-surfaceAlt">
                <td className={`${td} text-[#888] font-mono`}>{item.order_id}</td>
                <td className={`${td} text-muted`}>{item.date}</td>
                <td className={`${td} text-white`}>{item.channel}</td>
                <td className={`${td} text-muted`}>{tx.statuses[item.status] || item.status}</td>
                <td className={`${td} text-muted`}>{item.items}</td>
                <td className={`${td} text-white`}>{money(item.total)}</td>
              </tr>
            )))}

          {truncated && <p className="text-muted text-xs mt-3">{tx.truncated}</p>}

          <div className="flex flex-wrap items-center gap-3 mt-5">
            <button
              type="button"
              onClick={confirmImport}
              disabled={nothingToApply || !!busy}
              className={`font-bold text-sm px-5 py-2.5 rounded-lg ${nothingToApply ? 'bg-surfaceAlt text-muted cursor-not-allowed' : 'bg-accent text-panel cursor-pointer'}`}
            >
              {tx.confirm}
            </button>
            {nothingToApply && <span className="text-muted text-sm">{tx.nothing}</span>}
          </div>
        </div>
      )}

      <div className="mt-5">
        <button
          type="button"
          onClick={onClose}
          className="bg-transparent border border-edge text-muted text-xs px-3 py-1.5 rounded cursor-pointer"
        >
          {tx.cancel}
        </button>
      </div>
    </div>
  );
}

export default OrderImport;